/**
 * Ce que le rouleau montre d'avance, en secondes.
 *
 * Le roll étale cette durée sur toute sa hauteur, au tempo de référence du
 * morceau — passé là, elle devient une hauteur de ticks, voir
 * {@link rollTickSpan}. La tête de lecture en prend
 * une part pour montrer le passé (voir `playhead-distance.ts`), si bien que ce
 * qui est visible au-dessus d'elle dépend de la hauteur de l'écran, mais ne
 * dépasse jamais cette durée.
 *
 * # Pourquoi elle sort du composant
 *
 * Le live en a besoin sans traîner Pixi : un spectateur de récital recule
 * d'autant sa tête de lecture, pour que les notes de l'interprète tombent du
 * haut de son écran. Voir `lib/live/listener-horizon.ts`. Deux copies du même
 * nombre finiraient par diverger, et c'est la chute des notes qui le
 * montrerait.
 */
export const ROLL_VISIBLE_SECONDS = 4;

/** Le tempo de secours, quand la grille n'en donne pas d'utilisable. */
const FALLBACK_BPM = 120;

/** Le PPQ de secours, même raison. */
const FALLBACK_PPQ = 480;

/**
 * L'échelle du rouleau : combien de ticks sa hauteur porte.
 *
 * # Pourquoi une échelle en ticks, et pas en secondes
 *
 * Le rouleau place ses notes à la règle des ticks. Une fenêtre mesurée en
 * secondes à chaque image — « les 4 s qui viennent, au tempo courant » —
 * change donc d'échelle dès qu'un changement de tempo entre dans la fenêtre :
 * un tempo qui tombe de 120 à 60 divise par deux le nombre de ticks visibles,
 * et double la hauteur de tout ce qui est déjà à l'écran. L'étirement commence
 * quatre secondes avant le changement et dure jusqu'à ce qu'il soit franchi :
 * ce n'est pas un saut qu'on rate, c'est le rouleau qui gonfle sous les yeux.
 *
 * Fixée une fois par morceau, l'échelle rend le changement de tempo à ce qu'il
 * est musicalement : une noire garde sa hauteur d'un bout à l'autre, les
 * lignes de mesure gardent leur écart, et c'est le **défilement** qui ralentit
 * ou accélère. C'est aussi ce qu'on lit sur une partition qui défile.
 *
 * # Le tempo de référence
 *
 * Celui du début du morceau : la hauteur du rouleau vaut alors
 * {@link ROLL_VISIBLE_SECONDS} secondes de musique, et l'anticipation est
 * celle qui a été réglée. Un morceau qui accélère de moitié verra sa fenêtre
 * durer moins longtemps — c'est le prix d'une géométrie stable, et il se paie
 * en défilement plus rapide, ce que le joueur entend de toute façon.
 *
 * Le ralenti d'apprentissage ne passe pas par là : il joue sur
 * `LocalTransport.setPlaybackRate`, la grille ne bouge pas, donc l'échelle non
 * plus — la vue défile simplement moins vite, ce qui est déjà le bon geste.
 */
export function rollTickSpan(
  ppq: number,
  referenceBpm: number,
  visibleSeconds: number = ROLL_VISIBLE_SECONDS,
): number {
  const safePpq = ppq > 0 && Number.isFinite(ppq) ? ppq : FALLBACK_PPQ;
  const safeBpm = referenceBpm > 0 && Number.isFinite(referenceBpm) ? referenceBpm : FALLBACK_BPM;
  const safeSeconds = visibleSeconds > 0 && Number.isFinite(visibleSeconds)
    ? visibleSeconds
    : ROLL_VISIBLE_SECONDS;

  return safeSeconds * (safeBpm / 60) * safePpq;
}

/**
 * La vitesse de défilement du rouleau, en part de sa vitesse nominale.
 *
 * 1 quand le rouleau descend à la vitesse pour laquelle il est réglé — le
 * tempo de référence du morceau, à vitesse de lecture 1. 2 quand il descend
 * deux fois plus vite, qu'un tempo écrit l'ait doublé ou que le curseur de
 * vitesse le presse.
 *
 * Sert à ce qui doit vivre à l'horloge du rouleau plutôt qu'à celle de la
 * montre : les étincelles jaillies sous la tête de lecture, par exemple, qui
 * paraissaient molles sous des notes qui filent.
 *
 * C'est un rapport de tempo, pas une mesure du défilement réel : un rouleau
 * figé — vue décrochée, séance en pause — ne le met pas à zéro. On y joue
 * encore, et ce qui jaillit d'une touche doit garder l'allure du morceau.
 */
export function rollSpeedRatio(
  ticksPerSecond: number,
  tickSpan: number,
  visibleSeconds: number = ROLL_VISIBLE_SECONDS,
): number {
  if (!(tickSpan > 0) || !(ticksPerSecond >= 0) || !Number.isFinite(ticksPerSecond)) {
    return 1;
  }

  return (ticksPerSecond * visibleSeconds) / tickSpan;
}
